import { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material';
import { IDealOption } from '../../types/CreateDealForm';
import { IOption, IPortal, IProject } from '../../types/project';
import { IWishList } from '../../types/worksheet';
import { numToCurrency } from '../../utils/Functions';
import { AVAILABLEUNITTYPES } from './WishlistChoices';
import RentOptionCard, { setOptionRent } from './RentOptionCard';

// A wishlist worksheet can only add one of each option
const MAX_PER_OPTION = 1;

const numberWithCommas = (num: number) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Unit types picked in the wishlist that the option isn't available for (null when there are none)
const getUnitsNotIncluded = (unitTypes: string[], allowedUnits: string[]) => {
  const notIncluded = unitTypes.filter((unitType) => !allowedUnits.includes(unitType)).filter((unitType) => unitType);
  return notIncluded.length ? Array.from(new Set(notIncluded)) : null;
};

const cardStyle = { p: 2.5, border: '1px solid #c4c4c4', borderRadius: '4px', height: '100%' };
const cardTitleStyle = { m: 0, fontSize: '16px', fontWeight: 600, textTransform: 'uppercase' as const };

const WishlistOptions = (props: ChildProps) => {
  const { project, portal, choices, options, setOptions, joinWaitlist, setJoinWaitlist } = props;

  const { data } = useQuery<{ getAvailableUnitTypes: { unitType: string; size: number }[] }>(AVAILABLEUNITTYPES, {
    skip: !project._id,
    variables: { projectId: project._id },
  });
  const availableUnits = data?.getAvailableUnitTypes ?? [];
  const unitTypes = choices.map((choice) => choice.unitType);
  const displayPrice = !!portal?.showOptionsPrice;
  const displayOptions = !!portal?.showOptions;

  const findOption = (name: string) => options.find((option: IDealOption) => option.name === name);

  const handleAddOption = (option: IOption) => {
    const existingOption = findOption(option.name);
    const purchaseAmount = (existingOption?.purchaseAmount ?? 0) + 1;
    setOptions([
      ...options.filter((o: IDealOption) => o.name !== option.name),
      { ...existingOption, name: option.name, purchaseAmount, amount: purchaseAmount * option.price },
    ]);
  };

  const handleRemoveOption = (option: IOption) => {
    const existingOption = findOption(option.name);
    const otherOptions = options.filter((o: IDealOption) => o.name !== option.name);
    // Keep the rent choice even when the purchased amount goes back to none
    setOptions(
      existingOption?.rent
        ? [...otherOptions, { name: option.name, rent: existingOption.rent, purchaseAmount: 0, amount: 0 }]
        : otherOptions
    );
  };

  const handleJoinWaitlist = (title: string) => {
    setJoinWaitlist(joinWaitlist.includes(title) ? joinWaitlist.filter((waitlist) => waitlist !== title) : [...joinWaitlist, title]);
  };

  const residentialOptions = (project.options || []).filter((option: IOption) => option.type === 'residential');
  if (!residentialOptions.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {residentialOptions.map((option: IOption) => {
          const purchaseAmount = findOption(option.name)?.purchaseAmount ?? 0;
          const amount = findOption(option.name)?.amount ?? 0;
          const unitsNotIncluded = getUnitsNotIncluded(unitTypes, option.allowedUnits ?? []);
          const allowedSizes = option.allowedUnits?.length
            ? availableUnits.filter((unit) => option.allowedUnits.includes(unit.unitType)).map((unit) => unit.size)
            : [];
          const smallestAllowedSize = allowedSizes.length ? Math.min(...allowedSizes) : null;
          const errorText = !unitsNotIncluded
            ? ''
            : smallestAllowedSize !== null
              ? `Limited for Unit Sizes under ${numberWithCommas(smallestAllowedSize)}sf`
              : `Limited for ${unitsNotIncluded.join(', ')}`;

          return (
            <Fragment key={option.name}>
              <Grid item xs={12} sm={4}>
                <Box sx={cardStyle}>
                  <Typography component="h2" sx={cardTitleStyle}>
                    {displayPrice ? `${option.name} ${option.price ? numToCurrency.format(option.price) : ''}` : `Purchase ${option.name}`}
                  </Typography>
                  {displayOptions ? (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: '14px', mb: 0.5 }}>
                        {purchaseAmount > 0 ? `x${purchaseAmount} ${displayPrice ? `for $${amount}` : ''}` : 'NONE ADDED'}
                      </Typography>
                      {purchaseAmount > 0 ? (
                        <Button
                          variant="outlined"
                          color="error"
                          disableElevation
                          sx={{ mr: 0.5, minWidth: '15px' }}
                          onClick={() => handleRemoveOption(option)}
                        >
                          -
                        </Button>
                      ) : null}
                      {purchaseAmount < MAX_PER_OPTION ? (
                        <Button variant="contained" disableElevation sx={{ minWidth: '15px' }} onClick={() => handleAddOption(option)}>
                          +
                        </Button>
                      ) : null}
                      {errorText ? <Typography sx={{ color: '#f54394', fontSize: '12px', mt: 1 }}>{errorText}</Typography> : null}
                    </Box>
                  ) : (
                    <FormGroup sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={joinWaitlist.includes(option.name)}
                            onClick={() => handleJoinWaitlist(option.name)}
                          />
                        }
                        label="Join Waitlist"
                      />
                    </FormGroup>
                  )}
                </Box>
              </Grid>
              {option.rent ? (
                <Grid item xs={12} sm={4}>
                  <RentOptionCard
                    name={option.name}
                    rent={!!findOption(option.name)?.rent}
                    onChange={(rent) => setOptions(setOptionRent(options, option.name, rent))}
                  />
                </Grid>
              ) : null}
            </Fragment>
          );
        })}
      </Grid>
    </Box>
  );
};

interface ChildProps {
  project: IProject;
  portal?: Pick<IPortal, 'showOptions' | 'showOptionsPrice'>;
  choices: IWishList[];
  options: IDealOption[];
  setOptions: (options: IDealOption[]) => void;
  joinWaitlist: string[];
  setJoinWaitlist: (joinWaitlist: string[]) => void;
}

export default WishlistOptions;
